import { RadioChips } from "@/components/ax/RadioChips";
import { Switch } from "@/components/ax/Switch";
import { useI18n } from "@/i18n";
import { getThemePref, setThemePref } from "@/lib/prefs";
import { getCadenceOptIn, setCadenceOptIn } from "@/lib/accountStore";
import { useEffect, useState } from "react";
import { ChevronDown, Monitor, Moon, Palette, Sun } from "lucide-react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import styles from "@/components/account/AccountVisual.module.css";

export const Card = ({ id, title, summary, attention = false, busy = false, testId, children, icon: Icon }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (attention) setOpen(true); }, [attention]);
  return (
    <AccordionPrimitive.Root type="single" collapsible value={open ? id : ""} onValueChange={(value) => setOpen(!!value)}>
      <AccordionPrimitive.Item value={id} asChild>
        <section aria-labelledby={id} data-testid={testId} className={styles.card}>
          <AccordionPrimitive.Header asChild>
            <h2 className={styles.cardHeading}>
              <AccordionPrimitive.Trigger id={id} className={styles.cardTrigger} disabled={busy} aria-busy={busy || undefined} data-testid={`${testId}-toggle`}>
                {Icon && <span className={styles.cardIcon}><Icon size={18} strokeWidth={1.5} aria-hidden="true" /></span>}
                <span className={styles.cardHeadingText}><span className={styles.cardTitle}>{title}</span>{summary && <span className={styles.cardSummary}>{summary}</span>}</span>
                <ChevronDown size={18} strokeWidth={1.5} aria-hidden="true" className={styles.chevron} />
              </AccordionPrimitive.Trigger>
            </h2>
          </AccordionPrimitive.Header>
          <AccordionPrimitive.Content forceMount hidden={!open} className={styles.cardBody} data-testid={`${testId}-content`}>
            {children}
          </AccordionPrimitive.Content>
        </section>
      </AccordionPrimitive.Item>
    </AccordionPrimitive.Root>
  );
};

export const PreferencesCard = () => {
  const { t, lang, setLang } = useI18n();
  const [theme, setTheme] = useState(getThemePref);
  // Default mati. Tidak boleh sudah dicentang.
  const [cadence, setCadence] = useState(false);
  const [savingCadence, setSavingCadence] = useState(false);
  const [loadingCadence, setLoadingCadence] = useState(true);
  const [cadenceFailed, setCadenceFailed] = useState(false);

  useEffect(() => {
    let active = true;
    const q = new URLSearchParams(window.location.search);
    if (q.get("unsubscribe") === "cadence") {
      setCadenceOptIn(false).then(async (res) => {
        const value = res.ok ? false : await getCadenceOptIn();
        if (active) { setCadence(value); setCadenceFailed(!res.ok); setLoadingCadence(false); }
      });
      return () => { active = false; };
    }

    getCadenceOptIn().then((val) => {
      if (active) { setCadence(!!val); setLoadingCadence(false); }
    });
    return () => { active = false; };
  }, []);

  const handleCadenceChange = async (nextVal) => {
    if (savingCadence || loadingCadence) return;
    const prev = cadence;
    setSavingCadence(true);
    setCadenceFailed(false);
    const res = await setCadenceOptIn(nextVal);
    if (res.ok) {
      setCadence(nextVal);
    } else {
      setCadence(prev);
      setCadenceFailed(true);
    }
    setSavingCadence(false);
  };

  return (
    <Card id="acc-prefs" title={t("acc.prefs")} testId="account-prefs" icon={Palette} busy={savingCadence} attention={cadenceFailed}>
      <div className={styles.settings}>
        <RadioChips
          legend={t("acc.lang")}
          name="acc-lang"
          testId="pref-lang"
          className={styles.setting}
          value={lang}
          onChange={setLang}
          options={[{ value: "id", label: t("language.id") }, { value: "en", label: t("language.en") }]}
        />
        <RadioChips
          legend={t("acc.theme")}
          name="acc-theme"
          testId="pref-theme"
          className={styles.setting}
          value={theme}
          onChange={(v) => {
            setTheme(v);
            setThemePref(v);
          }}
          options={[["light", Sun], ["dark", Moon], ["system", Monitor]].map(([v, Icon]) => ({ value: v, label: <span className={styles.themeChoice}><Icon size={18} strokeWidth={1.5} aria-hidden="true" /><span>{t(`theme.${v}`)}</span></span> }))}
        />
        <div className={styles.settingRow}>
          <p id="cadence-label" className={styles.settingLabel}>{t("acc.cadence")}</p>
          <Switch
            checked={cadence}
            disabled={savingCadence || loadingCadence}
            busy={savingCadence || loadingCadence}
            labelledBy="cadence-label"
            testId="pref-cadence"
            onChange={handleCadenceChange}
          />
        </div>
        {cadenceFailed && <p role="alert" data-testid="cadence-error" className="m-0 text-sm leading-5 text-ax-error">{t("acc.cadenceFailed")}</p>}
      </div>
    </Card>
  );
};

